/* =========================================================
   Affiche ENFANT « Ma première saison ».
   API : SNPremiereSaison.render(data, opts) -> string SVG
     data : { prenom, sport, club, saison, groupe, age, phrase, coach, titre }
       sport = clé de PICTOS (judo, gymnastique, natation, basket, handball,
               equitation) — sinon médaille générique.
     opts : { palette }
   8 champs maximum : au-delà, la personnalisation sur téléphone fait fuir.

   RÈGLES DE DESSIN (contraintes de production, ne pas contourner) :
   - SVG PUR : aucune image, aucun fetch — le worker re-rend à l'impression.
   - Polices Sora + Oswald UNIQUEMENT, et AUCUN caractère hors de ces polices
     (une flèche ou un émoji s'imprime en carré vide — incident du 2026-08-05).
   - 1er élément = rect plein format de couleur unie (sert de passe-partout print).
   - Pictogrammes 100 % GÉOMÉTRIQUES : jamais de silhouette humaine (un enfant
     dessiné ne ressemblera jamais à l'enfant du client — on figure le SPORT,
     pas la personne).
   ========================================================= */
(function () {
  const PW = 800, PH = 1066;

  const PALETTES = {
    craie: { label: 'Craie', bg: '#F7F4ED', ink: '#0B2D5B', muted: '#8B94A3', accent: '#FF6B2C', line: '#E3DDD0', badge: '#FFFFFF', badgeLine: '#E3DDD0' },
    ciel:  { label: 'Ciel',  bg: '#EAF1F8', ink: '#0B2D5B', muted: '#7C8CA3', accent: '#FF6B2C', line: '#D3E0EC', badge: '#FFFFFF', badgeLine: '#D3E0EC' },
    corail:{ label: 'Corail',bg: '#FCEFE8', ink: '#0B2D5B', muted: '#A08579', accent: '#E2562A', line: '#F0DCD1', badge: '#FFFFFF', badgeLine: '#F0DCD1' },
    nuit:  { label: 'Nuit',  bg: '#0B2D5B', ink: '#F7F4ED', muted: '#93A1B8', accent: '#FF9E5C', line: '#1E4272', badge: '#12355F', badgeLine: '#25497A' },
  };

  const esc = s => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const up = s => String(s == null ? '' : s).toUpperCase();
  const fitSize = (text, maxW, maxSize, cwf, ls, minSize) => {
    const n = String(text).length || 1; minSize = minSize || 12;
    for (let s = maxSize; s > minSize; s -= 1) if (n * (cwf * s + ls) <= maxW) return s;
    return minSize;
  };

  /* --- Pictogrammes : dessinés dans un carré 200×200 centré sur (0,0),
         donc coordonnées de -100 à +100. Traits épais, formes simples,
         lisibles à 3 m sur un mur de chambre. --- */
  const PICTOS = {
    /* La veste entière est bien plus lisible qu'un col isolé : épaules, col
       croisé en V (creusé dans la couleur du médaillon) et ceinture nouée. */
    judo: (c, a, b) =>
        `<path d="M-58 -96 L58 -96 L96 -66 L74 -26 L62 -26 L62 76 L-62 76 L-62 -26 L-74 -26 L-96 -66 Z" fill="${c}"/>`
      + `<path d="M-30 -96 L30 -96 L6 -6 L-6 -6 Z" fill="${b}"/>`
      + `<path d="M-30 -96 L-6 -6 L-24 -6 L-46 -92 Z" fill="${a}"/>`
      + `<path d="M30 -96 L6 -6 L24 -6 L46 -92 Z" fill="${a}"/>`
      + `<rect x="-70" y="8" width="140" height="30" rx="8" fill="${a}"/>`
      + `<rect x="-24" y="2" width="48" height="42" rx="10" fill="${a}"/>`
      + `<path d="M-24 16 L24 10 L24 26 L-24 32 Z" fill="${c}" opacity="0.8"/>`,

    karate: (c, a) => /* ceinture nouée : pans EN DIAGONALE (des pans verticaux donnent un « T ») */
        `<path d="M-100 -52 L-32 -44 L-32 -6 L-100 -14 Z" fill="${a}"/>`
      + `<path d="M100 -52 L32 -44 L32 -6 L100 -14 Z" fill="${a}"/>`
      + `<path d="M-30 4 L-4 4 L-30 96 L-62 88 Z" fill="${a}"/>`
      + `<path d="M30 4 L4 4 L30 88 L60 78 Z" fill="${a}"/>`
      + `<rect x="-36" y="-50" width="72" height="60" rx="14" fill="${a}"/>`
      + `<path d="M-36 -30 L36 -22 L36 -6 L-36 -14 Z" fill="${c}" opacity="0.85"/>`,

    gymnastique: (c, a) => /* poutre : le cerceau se lisait « loupe », la poutre est sans ambiguïté */
        `<rect x="-102" y="-34" width="204" height="30" rx="8" fill="${c}"/>`
      + `<rect x="-102" y="-34" width="204" height="12" rx="6" fill="${a}"/>`
      + `<path d="M-64 -4 L-46 -4 L-30 80 L-50 80 Z" fill="${c}"/>`
      + `<path d="M64 -4 L46 -4 L30 80 L50 80 Z" fill="${c}"/>`
      + `<rect x="-62" y="80" width="44" height="14" rx="7" fill="${c}"/>`
      + `<rect x="18" y="80" width="44" height="14" rx="7" fill="${c}"/>`,

    /* Bassin : margelle pleine + eau + vagues. Les lignes d'eau en flotteurs
       (essai A/B) donnaient une grille de points ; les vagues disent « eau »
       tout de suite. Les lunettes, elles, formaient un visage. */
    natation: (c, a, b) =>
        `<rect x="-104" y="-70" width="208" height="140" rx="16" fill="${c}"/>`
      + `<rect x="-88" y="-54" width="176" height="108" rx="8" fill="${a}"/>`
      + `<path d="M-72 -24 q 18 -14 36 0 t 36 0 t 36 0 t 36 0" fill="none" stroke="${b}" stroke-width="9" stroke-linecap="round"/>`
      + `<path d="M-72 6 q 18 -14 36 0 t 36 0 t 36 0 t 36 0" fill="none" stroke="${b}" stroke-width="9" stroke-linecap="round"/>`
      + `<path d="M-72 36 q 18 -14 36 0 t 36 0 t 36 0 t 36 0" fill="none" stroke="${b}" stroke-width="9" stroke-linecap="round"/>`,

    basket: (c, a) => /* ballon + panneau */
        `<circle cx="0" cy="14" r="66" fill="none" stroke="${c}" stroke-width="13"/>`
      + `<line x1="-66" y1="14" x2="66" y2="14" stroke="${c}" stroke-width="9"/>`
      + `<line x1="0" y1="-52" x2="0" y2="80" stroke="${c}" stroke-width="9"/>`
      + `<path d="M-46 -32 q 46 46, 0 92" fill="none" stroke="${c}" stroke-width="8"/>`
      + `<path d="M46 -32 q -46 46, 0 92" fill="none" stroke="${c}" stroke-width="8"/>`
      + `<rect x="-52" y="-96" width="104" height="30" rx="6" fill="${a}"/>`,

    handball: (c, a) => /* but + ballon */
        `<rect x="-92" y="-56" width="184" height="112" rx="8" fill="none" stroke="${c}" stroke-width="13"/>`
      + `<line x1="-40" y1="-56" x2="-40" y2="56" stroke="${c}" stroke-width="6" opacity="0.5"/>`
      + `<line x1="18" y1="-56" x2="18" y2="56" stroke="${c}" stroke-width="6" opacity="0.5"/>`
      + `<line x1="-92" y1="0" x2="92" y2="0" stroke="${c}" stroke-width="6" opacity="0.5"/>`
      + `<circle cx="58" cy="46" r="30" fill="${a}"/>`,

    equitation: (c, a) => /* obstacle de saut */
        `<rect x="-88" y="-72" width="18" height="148" rx="6" fill="${c}"/>`
      + `<rect x="70" y="-72" width="18" height="148" rx="6" fill="${c}"/>`
      + `<rect x="-70" y="-46" width="140" height="15" rx="7" fill="${a}"/>`
      + `<rect x="-70" y="-6" width="140" height="15" rx="7" fill="${c}"/>`
      + `<rect x="-70" y="34" width="140" height="15" rx="7" fill="${a}"/>`
      + `<rect x="-104" y="76" width="50" height="14" rx="7" fill="${c}"/>`
      + `<rect x="54" y="76" width="50" height="14" rx="7" fill="${c}"/>`,

    /* Un but n'a PAS de barre en bas : le rectangle fermé se lisait « fenêtre ».
       Deux poteaux + une transversale, filet en retrait, ballon devant. */
    football: (c, a, b) =>
        `<g opacity="0.4">`
      + `<line x1="-78" y1="-58" x2="-78" y2="40" stroke="${c}" stroke-width="5"/>`
      + `<line x1="-39" y1="-58" x2="-39" y2="40" stroke="${c}" stroke-width="5"/>`
      + `<line x1="0" y1="-58" x2="0" y2="40" stroke="${c}" stroke-width="5"/>`
      + `<line x1="39" y1="-58" x2="39" y2="40" stroke="${c}" stroke-width="5"/>`
      + `<line x1="78" y1="-58" x2="78" y2="40" stroke="${c}" stroke-width="5"/>`
      + `<line x1="-96" y1="-26" x2="96" y2="-26" stroke="${c}" stroke-width="5"/>`
      + `<line x1="-96" y1="6" x2="96" y2="6" stroke="${c}" stroke-width="5"/>`
      + `</g>`
      + `<rect x="-104" y="-76" width="208" height="20" rx="9" fill="${c}"/>`
      + `<rect x="-104" y="-76" width="20" height="118" rx="9" fill="${c}"/>`
      + `<rect x="84" y="-76" width="20" height="118" rx="9" fill="${c}"/>`
      + `<circle cx="0" cy="56" r="46" fill="${a}"/>`
      + `<path d="M0 30 L25 48 L15 78 L-15 78 L-25 48 Z" fill="${c}"/>`,

    medaille: (c, a) => /* défaut : médaille */
        `<path d="M-46 -92 L-14 -18 L-78 -18 Z" fill="${a}"/>`
      + `<path d="M46 -92 L78 -18 L14 -18 Z" fill="${a}"/>`
      + `<circle cx="0" cy="30" r="60" fill="none" stroke="${c}" stroke-width="14"/>`
      + `<circle cx="0" cy="30" r="24" fill="${a}"/>`,
  };
  const SPORTS = {
    football: 'Football', judo: 'Judo', karate: 'Karaté', gymnastique: 'Gymnastique',
    natation: 'Natation', basket: 'Basket', handball: 'Handball', equitation: 'Équitation',
  };

  function render(data, opts) {
    data = data || {}; opts = opts || {};
    const p = PALETTES[opts.palette] || PALETTES.craie;
    const cx = PW / 2;

    const sportKey = String(data.sport || '').trim().toLowerCase();
    const picto = PICTOS[sportKey] || PICTOS.medaille;
    const sportNom = up(data.sportLabel) || up(SPORTS[sportKey]) || '';

    const titre = up(data.titre) || 'MA PREMIÈRE SAISON';
    const titreSize = fitSize(titre, 660, 26, 0.60, 6, 15);

    /* Médaillon : bas = BADGE_CY + BADGE_R = 480. Le prénom est en Oswald,
       dont la boîte d'ascente monte à ≈1,2 × la taille au-dessus de la ligne
       de base : à 110 px max et une ligne de base à 632, le haut des lettres
       (accents compris) reste à 500 — soit 20 px sous le médaillon. */
    const BADGE_CY = 330, BADGE_R = 150;
    const prenom = up(data.prenom).trim() || 'PRÉNOM';
    const prenomSize = fitSize(prenom, 680, 110, 0.54, 2, 42);

    const club = up(data.club).trim();
    const ligne2 = [sportNom, club].filter(Boolean).join(' · ');
    const ligne2Size = fitSize(ligne2, 660, 22, 0.58, 3, 13);

    /* Bandeau de 3 informations : on n'affiche que celles qui sont remplies,
       et on répartit l'espace entre elles. */
    const cases = [
      ['SAISON', up(data.saison).trim()],
      ['GROUPE', up(data.groupe).trim()],
      ['ÂGE', up(data.age).trim()],
    ].filter(c => c[1]);

    let bandeau = '';
    if (cases.length) {
      const x0 = 76, larg = (PW - 2 * x0) / cases.length;
      bandeau += `<line x1="${x0}" y1="782" x2="${PW - x0}" y2="782" stroke="${p.line}" stroke-width="1.5"/>`;
      cases.forEach((c, i) => {
        const centre = x0 + larg * i + larg / 2;
        if (i) bandeau += `<line x1="${(x0 + larg * i).toFixed(0)}" y1="812" x2="${(x0 + larg * i).toFixed(0)}" y2="892" stroke="${p.line}" stroke-width="1.5"/>`;
        const vs = fitSize(c[1], larg - 30, 40, 0.56, 0, 18);
        bandeau += `<text x="${centre.toFixed(0)}" y="840" font-size="14" font-weight="600" fill="${p.muted}" text-anchor="middle" letter-spacing="3">${esc(c[0])}</text>`
          + `<text x="${centre.toFixed(0)}" y="884" font-family="Oswald" font-size="${vs}" font-weight="700" fill="${p.ink}" text-anchor="middle">${esc(c[1])}</text>`;
      });
    }

    const phrase = String(data.phrase || '').trim();
    const phraseSize = fitSize(phrase, 640, 21, 0.52, 0, 13);
    const coach = up(data.coach).trim();

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${PW} ${PH}" font-family="'Sora','Inter',sans-serif">
<rect width="${PW}" height="${PH}" fill="${p.bg}"/>
<text x="${cx}" y="96" font-size="${titreSize}" font-weight="700" fill="${p.accent}" text-anchor="middle" letter-spacing="6">${esc(titre)}</text>
<circle cx="${cx}" cy="${BADGE_CY}" r="${BADGE_R}" fill="${p.badge}" stroke="${p.badgeLine}" stroke-width="2"/>
<g transform="translate(${cx} ${BADGE_CY}) scale(1.12)">${picto(p.ink, p.accent, p.badge)}</g>
<text x="${cx}" y="632" font-family="Oswald" font-size="${prenomSize}" font-weight="700" fill="${p.ink}" text-anchor="middle" letter-spacing="1">${esc(prenom)}</text>
${ligne2 ? `<text x="${cx}" y="684" font-size="${ligne2Size}" font-weight="600" fill="${p.muted}" text-anchor="middle" letter-spacing="3">${esc(ligne2)}</text>` : ''}
${bandeau}
${phrase ? `<text x="${cx}" y="962" font-size="${phraseSize}" font-weight="600" fill="${p.ink}" text-anchor="middle">${esc(phrase)}</text>` : ''}
${coach ? `<text x="${cx}" y="1008" font-size="15" font-weight="600" fill="${p.muted}" text-anchor="middle" letter-spacing="3">COACH ${esc(coach)}</text>` : ''}
</svg>`;
  }

  window.SNPremiereSaison = { render, PALETTES, SPORTS };
})();
